// UserDetail.jsx - Perbaikan import Badge
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Button, Card, Alert, Row, Col, Table, Tabs, Tab, Modal, Badge } from "react-bootstrap";
import { FaCheck, FaTimes, FaTrash, FaEnvelope } from "react-icons/fa";
import axios from "axios";
import useAuth from "../hooks/useAuth";

function UserDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { user: currentUser } = useAuth();
	const [showAccessRequest, setShowAccessRequest] = useState(false);
	const [accessRequestData, setAccessRequestData] = useState(null);
	const [accessRequests, setAccessRequests] = useState([]);

	const [user, setUser] = useState(null);
	const [permissions, setPermissions] = useState([]);
	const [bots, setBots] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [activeTab, setActiveTab] = useState("general");

	const [formData, setFormData] = useState({
		username: "",
		email: "",
		role: "user",
		password: "",
	});

	useEffect(() => {
		Promise.all([fetchUser(), fetchBots(), fetchAccessRequests()]);

		// Check if we have access request data from navigation
		if (location.state?.showAccessRequest) {
			setShowAccessRequest(true);
			setAccessRequestData(location.state.requestData);

			// Clear the location state to prevent showing the request again after refresh
			navigate(location.pathname, { replace: true });
		}
	}, [id, location.state]);

	const fetchUser = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`/api/users/${id}`);
			setUser(response.data.user);
			setPermissions(response.data.permissions || []);

			setFormData({
				username: response.data.user.username,
				email: response.data.user.email,
				role: response.data.user.role,
				password: "",
			});

			setError(null);
		} catch (error) {
			console.error("Failed to fetch user:", error);
			setError(error.response?.data?.message || "Failed to load user details");
		} finally {
			setLoading(false);
		}
	};

	const fetchBots = async () => {
		try {
			const response = await axios.get("/api/bots");
			setBots(response.data.bots);
		} catch (error) {
			console.error("Failed to fetch bots:", error);
		}
	};

	const fetchAccessRequests = async () => {
		try {
			// Get all access requests for this user
			const response = await axios.get(`/api/bot-requests/user/${id}`);

			if (response.data.requests && response.data.requests.length > 0) {
				setAccessRequests(response.data.requests);

				// If we don't already have a specific request to show,
				// show the most recent pending one
				if (!showAccessRequest) {
					const pendingRequest = response.data.requests.find((r) => r.status === "pending");
					if (pendingRequest) {
						setShowAccessRequest(true);
						setAccessRequestData({
							id: pendingRequest.id,
							reason: pendingRequest.reason,
							username: user?.username || "User",
							timestamp: pendingRequest.created_at,
						});
					}
				}
			}
		} catch (error) {
			console.error("Failed to fetch access requests:", error);
		}
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			setLoading(true);
			setError(null);
			setSuccess(null);

			const payload = { ...formData };
			if (!payload.password) {
				delete payload.password;
			}

			await axios.put(`/api/users/${id}`, payload);
			setSuccess("User updated successfully");
			fetchUser();
		} catch (error) {
			console.error("Failed to update user:", error);
			setError(error.response?.data?.message || "Failed to update user");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async () => {
		if (window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
			try {
				setLoading(true);
				await axios.delete(`/api/users/${id}`);
				navigate("/admin/users");
			} catch (error) {
				console.error("Failed to delete user:", error);
				setError(error.response?.data?.message || "Failed to delete user");
				setLoading(false);
			}
		}
	};

	const updatePermission = async (botId, field, value) => {
		try {
			setLoading(true);

			// Find existing permission for this bot
			const existingPerm = permissions.find((p) => p.bot_id === botId);

			// Create permission data
			const permData = {
				botId,
				canView: existingPerm?.can_view || false,
				canStart: existingPerm?.can_start || false,
				canStop: existingPerm?.can_stop || false,
				[field]: value,
			};

			await axios.post(`/api/users/${id}/permissions`, permData);

			// Update local permissions state
			setPermissions((prev) => {
				const newPerms = [...prev];
				const index = newPerms.findIndex((p) => p.bot_id === botId);

				if (index !== -1) {
					// Update existing permission
					newPerms[index] = {
						...newPerms[index],
						[field]: value,
					};
				} else {
					// Add new permission
					newPerms.push({
						bot_id: botId,
						bot_name: bots.find((b) => b.id === botId)?.name,
						[field]: value,
					});
				}

				return newPerms;
			});

			setSuccess("Permissions updated successfully");
		} catch (error) {
			console.error("Failed to update permissions:", error);
			setError(error.response?.data?.message || "Failed to update permissions");
		} finally {
			setLoading(false);
		}
	};

	const handleApproveAccessRequest = async () => {
		if (!accessRequestData || !accessRequestData.id) {
			setError("Invalid access request data");
			return;
		}

		try {
			setLoading(true);

			// Approve access request
			await axios.put(`/api/bot-requests/${accessRequestData.id}`, {
				status: "approved",
				permissions: bots.map((bot) => ({
					bot_id: bot.id,
					can_view: true,
					can_start: false,
					can_stop: false,
				})),
				notifyUser: true, // Add this flag to send notification to user
			});

			setSuccess("Access request approved successfully! Basic view permissions have been granted and the user has been notified.");
			setShowAccessRequest(false);

			// Refresh permissions data
			fetchUser();
			fetchAccessRequests();
		} catch (error) {
			console.error("Failed to approve access request:", error);
			setError(error.response?.data?.message || "Failed to approve access request");
		} finally {
			setLoading(false);
		}
	};

	const handleRejectAccessRequest = async () => {
		if (!accessRequestData || !accessRequestData.id) {
			setError("Invalid access request data");
			return;
		}

		try {
			setLoading(true);

			// Reject access request
			await axios.put(`/api/bot-requests/${accessRequestData.id}`, {
				status: "rejected",
				notifyUser: true, // Add this flag to send notification to user
			});

			setSuccess("Access request rejected. User has been notified.");
			setShowAccessRequest(false);

			// Refresh access requests
			fetchAccessRequests();
		} catch (error) {
			console.error("Failed to reject access request:", error);
			setError(error.response?.data?.message || "Failed to reject access request");
		} finally {
			setLoading(false);
		}
	};

	// Function to manually close the access request alert
	const handleCloseAccessRequest = () => {
		setShowAccessRequest(false);
	};

	if (loading && !user) {
		return <div className="text-center py-5">Loading...</div>;
	}

	if (error && !user) {
		return (
			<Alert variant="danger" className="m-3">
				{error}
			</Alert>
		);
	}

	if (currentUser?.id === parseInt(id)) {
		return (
			<Alert variant="warning" className="m-3">
				You cannot edit your own user account. Please use another admin account to make changes.
			</Alert>
		);
	}

	return (
		<div className="user-detail-page">
			<h1 className="mb-4">Edit User</h1>

			{error && (
				<Alert variant="danger" onClose={() => setError(null)} dismissible>
					{error}
				</Alert>
			)}
			{success && (
				<Alert variant="success" onClose={() => setSuccess(null)} dismissible>
					{success}
				</Alert>
			)}

			{/* Access Request Alert with dismiss button */}
			{showAccessRequest && accessRequestData && (
				<Alert variant="info" className="mb-4" onClose={handleCloseAccessRequest} dismissible>
					<div className="d-flex align-items-start">
						<div className="me-3">
							<FaEnvelope size={24} />
						</div>
						<div className="flex-grow-1">
							<h5 className="mb-2">Access Request from {accessRequestData.username}</h5>
							<p>
								<strong>Reason:</strong> {accessRequestData.reason}
							</p>
							<p>
								<strong>Requested at:</strong> {new Date(accessRequestData.timestamp).toLocaleString()}
							</p>
							<div className="mt-3">
								<Button variant="success" className="me-2" onClick={handleApproveAccessRequest} disabled={loading}>
									<FaCheck className="me-1" /> Approve with Basic Access
								</Button>
								<Button variant="danger" onClick={handleRejectAccessRequest} disabled={loading}>
									<FaTimes className="me-1" /> Reject
								</Button>
							</div>
						</div>
					</div>
				</Alert>
			)}

			<Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
				<Tab eventKey="general" title="General">
					<Card className="mb-4">
						<Card.Header>User Information</Card.Header>
						<Card.Body>
							<Form onSubmit={handleSubmit}>
								<Form.Group className="mb-3">
									<Form.Label>Username</Form.Label>
									<Form.Control type="text" name="username" value={formData.username} onChange={handleChange} required />
								</Form.Group>

								<Form.Group className="mb-3">
									<Form.Label>Email</Form.Label>
									<Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required />
								</Form.Group>

								<Form.Group className="mb-3">
									<Form.Label>Role</Form.Label>
									<Form.Select name="role" value={formData.role} onChange={handleChange}>
										<option value="user">User</option>
										<option value="admin">Admin</option>
									</Form.Select>
								</Form.Group>

								<Form.Group className="mb-3">
									<Form.Label>Password</Form.Label>
									<Form.Control
										type="password"
										name="password"
										value={formData.password}
										onChange={handleChange}
										placeholder="Leave blank to keep current password"
									/>
								</Form.Group>

								<div className="d-flex justify-content-between">
									<Button variant="primary" type="submit" disabled={loading}>
										{loading ? "Saving..." : "Save Changes"}
									</Button>

									<Button variant="outline-danger" onClick={handleDelete} disabled={loading}>
										<FaTrash /> Delete User
									</Button>
								</div>
							</Form>
						</Card.Body>
					</Card>
				</Tab>
				<Tab eventKey="permissions" title="Bot Permissions">
					<Card>
						<Card.Header>Bot Permissions</Card.Header>
						<Card.Body>
							{bots.length === 0 ? (
								<p className="text-center text-muted">No bots available</p>
							) : (
								<Table striped bordered hover>
									<thead>
										<tr>
											<th>Bot</th>
											<th>View</th>
											<th>Start</th>
											<th>Stop</th>
										</tr>
									</thead>
									<tbody>
										{bots.map((bot) => {
											const perm = permissions.find((p) => p.bot_id === bot.id);

											return (
												<tr key={bot.id}>
													<td>{bot.name}</td>
													<td className="text-center">
														<Button
															variant={perm?.can_view ? "success" : "outline-secondary"}
															size="sm"
															onClick={() => updatePermission(bot.id, "canView", !perm?.can_view)}
															disabled={loading}
														>
															{perm?.can_view ? <FaCheck /> : <FaTimes />}
														</Button>
													</td>
													<td className="text-center">
														<Button
															variant={perm?.can_start ? "success" : "outline-secondary"}
															size="sm"
															onClick={() => updatePermission(bot.id, "canStart", !perm?.can_start)}
															disabled={loading}
														>
															{perm?.can_start ? <FaCheck /> : <FaTimes />}
														</Button>
													</td>
													<td className="text-center">
														<Button
															variant={perm?.can_stop ? "success" : "outline-secondary"}
															size="sm"
															onClick={() => updatePermission(bot.id, "canStop", !perm?.can_stop)}
															disabled={loading}
														>
															{perm?.can_stop ? <FaCheck /> : <FaTimes />}
														</Button>
													</td>
												</tr>
											);
										})}
									</tbody>
								</Table>
							)}
						</Card.Body>
					</Card>
				</Tab>
				<Tab eventKey="requests" title="Access Requests">
					<Card>
						<Card.Header>Access Request History</Card.Header>
						<Card.Body>
							{accessRequests.length === 0 ? (
								<p className="text-center text-muted">No access requests found for this user</p>
							) : (
								<Table striped bordered hover>
									<thead>
										<tr>
											<th>Date</th>
											<th>Reason</th>
											<th>Status</th>
											<th>Updated</th>
										</tr>
									</thead>
									<tbody>
										{accessRequests.map((request) => (
											<tr key={request.id}>
												<td>
													{new Date(request.created_at).toLocaleDateString()} {new Date(request.created_at).toLocaleTimeString()}
												</td>
												<td>{request.reason}</td>
												<td>
													<Badge bg={request.status === "approved" ? "success" : request.status === "rejected" ? "danger" : "warning"}>
														{request.status}
													</Badge>
												</td>
												<td>
													{request.updated_at
														? `${new Date(request.updated_at).toLocaleDateString()} ${new Date(request.updated_at).toLocaleTimeString()}`
														: "-"}
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							)}
						</Card.Body>
					</Card>
				</Tab>
			</Tabs>
		</div>
	);
}

export default UserDetail;
